/**
 * Product-card quantity selector
 *
 * – Intercepts the add-to-cart click (capture phase).
 * – Adds the item to cart (qty 1) via /cart/add.js, then shows
 *   the quantity selector overlay for further adjustments.
 * – The <quantity-selector> custom element (from app.js) handles +/–.
 * – On quantity change, a debounced /cart/change.js updates the line item.
 * – The .remove button sets quantity to 0 (removes from cart) and hides overlay.
 */
if (!window.__productGridQtyInit) {
  window.__productGridQtyInit = true;

  const _qtyTimers = new WeakMap();
  let _addToCartPromise = null;

  function addToCart(variantId) {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);

    _addToCartPromise = fetch(theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' },
      body: formData
    })
      .then((r) => r.json())
      .then((response) => {
        if (!response.status) {
          dispatchCustomEvent('cart:item-added', {
            product: response.hasOwnProperty('items') ? response.items[0] : response
          });
          dispatchCustomEvent('cart:refresh');
        }
        return response;
      });

    return _addToCartPromise;
  }

  function debouncedCartUpdate(container, variantId, quantity) {
    clearTimeout(_qtyTimers.get(container));
    const timer = setTimeout(async () => {
      _qtyTimers.delete(container);

      /* Wait for addToCart to finish so the item exists in cart */
      if (_addToCartPromise) {
        try { await _addToCartPromise; } catch (e) { /* ignore */ }
        _addToCartPromise = null;
      }

      fetch(theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: quantity })
      })
        .then((r) => r.json())
        .then((cart) => {
          dispatchCustomEvent('cart:refresh');
          syncOverlays(cart);
        })
        .catch(console.error);
    }, 200);
    _qtyTimers.set(container, timer);
  }

  /* ── Intercept add-to-cart click (capture phase) ── */
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('.product-card--add-to-cart-button');
      if (!button) return;

      const card = button.closest('.product-card');
      if (!card) return;

      const container = card.querySelector('.product-card-quantity-selector-container');
      if (!container) return;

      /* Block the native add-to-cart completely */
      event.preventDefault();
      event.stopImmediatePropagation();

      const variantId = container.dataset.variantId;
      if (!variantId) return;

      /* Add item to cart (qty 1), show the overlay immediately */
      container.classList.add('is-visible');

      addToCart(variantId)
        .catch(console.error);
    },
    true /* capture phase */
  );

  /* ── Listen for quantity changes from <quantity-selector> ── */
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!input.classList.contains('qty')) return;

    const container = input.closest('.product-card-quantity-selector-container');
    if (!container) return;

    const variantId = container.dataset.variantId;
    if (!variantId) return;

    const quantity = parseInt(input.value, 10) || 1;
    debouncedCartUpdate(container, variantId, quantity);
  });

  /* ── Remove / reset button ── */
  document.addEventListener('click', (event) => {
    const removeBtn = event.target.closest('.product-card-quantity-selector-container .remove');
    if (!removeBtn) return;

    event.preventDefault();
    event.stopPropagation();

    const container = removeBtn.closest('.product-card-quantity-selector-container');
    if (!container) return;

    const variantId = container.dataset.variantId;

    /* Reset input value back to 1 */
    const input = container.querySelector('.qty');
    if (input) {
      input.value = 1;
    }

    /* Remove from cart (quantity = 0) */
    if (variantId) {
      debouncedCartUpdate(container, variantId, 0);
    }

    /* Hide the overlay */
    container.classList.remove('is-visible');
  });

  /* ── Sync overlays when cart changes elsewhere (e.g. cart drawer) ── */
  function syncOverlays(cart) {
    /* Build a map: variantId → quantity */
    const cartMap = {};
    cart.items.forEach((item) => {
      cartMap[String(item.variant_id)] = item.quantity;
    });

    document.querySelectorAll('.product-card-quantity-selector-container').forEach((container) => {
      /* Skip containers with a pending debounce — user is still clicking */
      if (_qtyTimers.has(container)) return;

      const vid = container.dataset.variantId;
      if (!vid) return;

      const input = container.querySelector('.qty');
      const qty = cartMap[vid] || 0;

      if (qty > 0) {
        if (input) input.value = qty;
        container.classList.add('is-visible');
      } else {
        if (input) input.value = 1;
        container.classList.remove('is-visible');
      }
    });
  }

  /* When cart drawer updates a line item (qty change / remove), sync from event data */
  document.addEventListener('line-item:change:end', (event) => {
    if (event.detail?.cart) {
      syncOverlays(event.detail.cart);
    }
  });
}

